import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../lib/i18n';
import { ImageOpenError, openImage, type ImageFailure } from '../lib/images';

/** The side of the avatar we save, in pixels. */
const OUTPUT_SIZE = 512;
const MAX_ZOOM = 5;

interface Props {
  file: File;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

interface Placement {
  /** Pixels of the source image per pixel of the viewport. */
  scale: number;
  /** Top-left of the scaled image, relative to the viewport. */
  x: number;
  y: number;
}

/**
 * Chooses the square of a photo that becomes the avatar.
 *
 * A phone camera takes a tall rectangle and an avatar is a small circle, so
 * using the whole file meant squashing a person into a shape their photo was
 * never in — a head at the top of a portrait shot came out as a chin and a
 * ceiling. Letting the player drag and pinch until their face is in the ring
 * is the only part of this they actually care about.
 *
 * The crop happens here rather than on the server because the answer is a
 * matter of taste, not of policy: only the person looking at the picture knows
 * which bit of it is them. Sending a 512px square also means a 4 MB holiday
 * photo never has to leave the phone.
 */
export function AvatarCropper({ file, onCancel, onCropped }: Props) {
  const t = useT();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState<ImageFailure | null>(null);
  const [box, setBox] = useState(280);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  const frame = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(1);
  // Live pointers, so one finger pans and two pinch, using the same events.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);

  useEffect(() => {
    let live = true;
    let release: (() => void) | null = null;
    setImage(null);
    setFailed(null);

    void openImage(file)
      .then((opened) => {
        // Nothing to show any more: let go of the object URL rather than
        // holding the whole photo in memory until the tab is closed.
        if (!live) {
          opened.release();
          return;
        }
        release = opened.release;
        setImage(opened.image);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setFailed(error instanceof ImageOpenError ? error.kind : 'decode');
      });

    return () => {
      live = false;
      release?.();
    };
  }, [file]);

  // The viewport is square and as wide as there is room for, so the same
  // component works on a phone held in one hand and on a scoreboard.
  useEffect(() => {
    const measure = (): void => {
      const width = frame.current?.clientWidth;
      if (width) setBox(Math.max(160, Math.min(width, 320)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [image]);

  /** The zoom at which the image exactly covers the viewport. */
  const baseScale = useMemo(() => {
    if (!image) return 1;
    return Math.max(box / image.naturalWidth, box / image.naturalHeight);
  }, [image, box]);

  const clamp = useCallback(
    (next: { x: number; y: number }, scale: number): { x: number; y: number } => {
      if (!image) return next;
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      // Never let a gap open at an edge: the ring is always full of photo.
      return {
        x: Math.min(0, Math.max(box - width, next.x)),
        y: Math.min(0, Math.max(box - height, next.y)),
      };
    },
    [image, box],
  );

  // Start centred, which is where the subject of a snapshot usually is.
  useEffect(() => {
    if (!image) return;
    const scale = baseScale;
    zoomRef.current = 1;
    setZoom(1);
    setOffset({
      x: (box - image.naturalWidth * scale) / 2,
      y: (box - image.naturalHeight * scale) / 2,
    });
  }, [image, baseScale, box]);

  /**
   * Zooms about the centre of the ring, so the face does not walk off.
   *
   * The live zoom is kept in a ref as well as in state because a pinch fires
   * several pointer moves between two renders, and each of them has to zoom
   * from where the last one left off rather than from where the last paint
   * was.
   */
  const zoomTo = useCallback(
    (next: number): void => {
      if (!image) return;
      const wanted = Math.min(MAX_ZOOM, Math.max(1, next));
      const from = baseScale * zoomRef.current;
      const to = baseScale * wanted;
      zoomRef.current = wanted;
      setOffset((o) =>
        clamp(
          {
            x: box / 2 - ((box / 2 - o.x) / from) * to,
            y: box / 2 - ((box / 2 - o.y) / from) * to,
          },
          to,
        ),
      );
      setZoom(wanted);
    },
    [image, baseScale, box, clamp],
  );

  const spread = (): number => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) pinch.current = { distance: spread(), zoom: zoomRef.current };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const previous = pointers.current.get(e.pointerId);
    if (!previous) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const start = pinch.current;
      if (start && start.distance > 0) zoomTo((start.zoom * spread()) / start.distance);
      return;
    }

    const dx = e.clientX - previous.x;
    const dy = e.clientY - previous.y;
    setOffset((o) => clamp({ x: o.x + dx, y: o.y + dy }, baseScale * zoomRef.current));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const placement: Placement = { scale: baseScale * zoom, x: offset.x, y: offset.y };

  const cut = (): void => {
    if (!image) return;
    setBusy(true);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setBusy(false);
      setFailed('decode');
      return;
    }
    // What the viewport shows, back in the coordinates of the original file.
    // Rounding is nudged back inside the image so a crop pushed hard against
    // an edge cannot ask for a fraction of a pixel that is not there.
    const source = box / placement.scale;
    const sx = Math.min(
      Math.max(0, -placement.x / placement.scale),
      Math.max(0, image.naturalWidth - source),
    );
    const sy = Math.min(
      Math.max(0, -placement.y / placement.scale),
      Math.max(0, image.naturalHeight - source),
    );
    ctx.drawImage(image, sx, sy, source, source, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    canvas.toBlob(
      (blob) => {
        setBusy(false);
        if (!blob) {
          setFailed('decode');
          return;
        }
        // Always JPEG: a photograph is what this is, and it keeps a cropped
        // PNG screenshot from arriving as a megabyte of flat colour.
        onCropped(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4">
      <div className="panel flex w-full max-w-sm flex-col gap-4">
        <h2 className="text-lg font-bold">{t('crop.title')}</h2>

        <div ref={frame} className="flex flex-col items-center gap-3">
          {/* Two different failures, because they have two different
              answers: a HEIC that even the converter could not read is worth
              re-saving as a JPEG on the phone, and anything else is not the
              player's fault at all. */}
          {failed && (
            <p className="text-balance text-center text-sm text-ball-red">
              {t(failed === 'heic' ? 'crop.failedHeic' : 'crop.failed')}
            </p>
          )}
          {!image && !failed && <p className="text-sm text-white/60">{t('crop.loading')}</p>}

          {image && !failed && (
            <>
              <div
                className="relative touch-none overflow-hidden rounded-2xl bg-black/60"
                style={{ width: box, height: box, cursor: 'grab' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onWheel={(e) => zoomTo(zoomRef.current * (e.deltaY < 0 ? 1.1 : 1 / 1.1))}
              >
                <img
                  src={image.src}
                  alt=""
                  draggable={false}
                  className="pointer-events-none absolute origin-top-left select-none"
                  style={{
                    left: placement.x,
                    top: placement.y,
                    width: image.naturalWidth * placement.scale,
                    height: image.naturalHeight * placement.scale,
                    maxWidth: 'none',
                  }}
                />
                {/* The circle is a hole in a dark sheet: what falls outside
                    the avatar stays visible, but obviously is not part of it.
                    Both the rim and the sheet are one shadow, because a
                    Tailwind ring is a box-shadow too and the second one wins. */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.7), 0 0 0 9999px rgba(0,0,0,0.55)',
                  }}
                />
              </div>

              <label className="flex w-full items-center gap-3">
                <span className="text-xs text-white/60">{t('crop.zoom')}</span>
                <input
                  type="range"
                  min={1}
                  max={MAX_ZOOM}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => zoomTo(Number(e.target.value))}
                  className="h-2 flex-1 accent-white"
                />
              </label>
              <p className="text-xs text-white/40">{t('crop.hint')}</p>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost flex-1">
            {t('crop.cancel')}
          </button>
          <button
            type="button"
            disabled={!image || failed !== null || busy}
            onClick={cut}
            className="btn flex-1 bg-white text-hud-bg"
          >
            {t('crop.use')}
          </button>
        </div>
      </div>
    </div>
  );
}
